/**
 * TeacherDrawer – everything a school does with one teacher, in one place.
 *
 * A pending teacher is approved and given their classes in the same step (the
 * old flow approved on one page and assigned on another). An approved teacher's
 * classes are edited here, or the approval revoked, which also takes them off
 * their classes. All changes go through /school-roster.
 */

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import type { Class } from '../../types/lms';
import type { RosterPerson, SchoolRoster } from '../../hooks/useSchoolRoster';
import { changeClassTeachers, decideTeacher } from '../../services/schoolRosterService';
import { MAX_CLASS_SIZE } from '../../services/classManagementService';

export function StatusBadge({ status }: { status: RosterPerson['approvalStatus'] }) {
  if (status === 'approved') return <Badge variant="secondary">Approved</Badge>;
  if (status === 'rejected') return <Badge variant="destructive">Rejected</Badge>;
  return <Badge variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400">Pending</Badge>;
}

const idsOf = (list: Class[] | undefined) => new Set((list ?? []).map((c) => c.id));

export function TeacherDrawer({
  teacher,
  roster,
  schoolId,
  onClose,
}: {
  teacher: RosterPerson | null;
  roster: SchoolRoster;
  /** Named for admins and partners; the server ignores it for school staff. */
  schoolId: string;
  onClose: () => void;
}) {
  const initialTeaches = useMemo(() => idsOf(teacher ? roster.classesByTeacher.get(teacher.id) : []), [teacher, roster.classesByTeacher]);
  const initialLeads = useMemo(() => idsOf(teacher ? roster.classTeacherOf.get(teacher.id) : []), [teacher, roster.classTeacherOf]);

  const [teaches, setTeaches] = useState<Set<string>>(new Set());
  const [leads, setLeads] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState<'reject' | 'revoke' | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset when a different teacher is opened, not on every roster snapshot,
  // so a live update does not wipe choices being made.
  useEffect(() => {
    setTeaches(new Set(initialTeaches));
    setLeads(new Set(initialLeads));
    setConfirming(null);
    setReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacher?.id]);

  if (!teacher) return null;
  const status = teacher.approvalStatus;
  const isApproved = status === 'approved';

  const without = (set: Set<string>, id: string) => {
    const next = new Set(set);
    next.delete(id);
    return next;
  };
  // Not teaching a class means not leading it; leading one means teaching it.
  const toggleTeaches = (id: string) => {
    if (teaches.has(id)) {
      setTeaches(without(teaches, id));
      setLeads(without(leads, id));
    } else setTeaches(new Set(teaches).add(id));
  };
  const toggleLeads = (id: string) => {
    if (leads.has(id)) setLeads(without(leads, id));
    else {
      setLeads(new Set(leads).add(id));
      setTeaches(new Set(teaches).add(id));
    }
  };

  const run = async (work: () => Promise<boolean>) => {
    setBusy(true);
    try {
      if (await work()) onClose();
    } finally {
      setBusy(false);
    }
  };

  const approve = () =>
    run(async () => {
      const result = await decideTeacher(teacher.id, 'approve', {
        schoolId,
        classIds: [...teaches],
        classTeacherOf: [...leads],
      });
      if (!result.ok) {
        toast.error(result.message || 'Could not approve the teacher');
        return false;
      }
      toast.success(
        teaches.size ? `${teacher.name} approved and assigned to ${teaches.size} class${teaches.size === 1 ? '' : 'es'}` : `${teacher.name} approved`
      );
      return true;
    });

  const decline = (decision: 'reject' | 'revoke') =>
    run(async () => {
      const result = await decideTeacher(teacher.id, decision, { schoolId, reason: reason.trim() || undefined });
      if (!result.ok) {
        toast.error(result.message || `Could not ${decision} the teacher`);
        return false;
      }
      const detached = result.data?.classIds.length ?? 0;
      toast.success(
        decision === 'reject'
          ? `${teacher.name} rejected`
          : `Approval revoked${detached ? ` and removed from ${detached} class${detached === 1 ? '' : 'es'}` : ''}`
      );
      return true;
    });

  /** Approved teacher: one request per class that actually changed. */
  const saveClasses = () =>
    run(async () => {
      const changed = roster.classes.filter(
        (c) => teaches.has(c.id) !== initialTeaches.has(c.id) || leads.has(c.id) !== initialLeads.has(c.id)
      );
      if (changed.length === 0) return true;
      const failures: string[] = [];
      for (const c of changed) {
        const nowTeaches = teaches.has(c.id);
        const leadChanged = leads.has(c.id) !== initialLeads.has(c.id);
        const result = await changeClassTeachers(c.id, {
          schoolId,
          ...(nowTeaches && !initialTeaches.has(c.id) ? { add: [teacher.id] } : {}),
          ...(!nowTeaches ? { remove: [teacher.id] } : {}),
          ...(nowTeaches && leadChanged ? { classTeacherId: leads.has(c.id) ? teacher.id : null } : {}),
        });
        if (!result.ok) failures.push(`${c.class_name}: ${result.message}`);
      }
      if (failures.length) {
        toast.error(`Some classes were not updated — ${failures.join('; ')}`);
        return false;
      }
      toast.success(`${teacher.name}'s classes updated`);
      return true;
    });

  const dirty =
    [...teaches].some((id) => !initialTeaches.has(id)) ||
    [...initialTeaches].some((id) => !teaches.has(id)) ||
    [...leads].some((id) => !initialLeads.has(id)) ||
    [...initialLeads].some((id) => !leads.has(id));

  return (
    <Sheet open onOpenChange={(open) => !open && !busy && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-lg">
        <SheetHeader className="space-y-1 text-left">
          <div className="flex items-center gap-2">
            <SheetTitle className="truncate">{teacher.name}</SheetTitle>
            <StatusBadge status={status} />
          </div>
          <SheetDescription className="truncate">{teacher.email || 'No email on file'}</SheetDescription>
        </SheetHeader>

        <dl className="mt-4 grid grid-cols-2 gap-3 rounded-lg border border-border p-3 text-xs">
          <div>
            <dt className="text-muted-foreground">Subjects</dt>
            <dd className="mt-0.5 text-foreground">{teacher.subjects.length ? teacher.subjects.join(', ') : '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Signed up</dt>
            <dd className="mt-0.5 text-foreground">{teacher.createdAt ? new Date(teacher.createdAt).toLocaleDateString() : '—'}</dd>
          </div>
          {status === 'rejected' && teacher.rejectionReason && (
            <div className="col-span-2">
              <dt className="text-muted-foreground">Reason given</dt>
              <dd className="mt-0.5 text-foreground">{teacher.rejectionReason}</dd>
            </div>
          )}
        </dl>

        <section className="mt-5">
          <h3 className="text-sm font-semibold text-foreground">{isApproved ? 'Classes' : 'Assign classes (optional)'}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {isApproved
              ? 'Tick the classes this teacher teaches. A class teacher approves the class’s students.'
              : 'Give the teacher their classes as you approve them.'}
          </p>
          {roster.classes.length === 0 ? (
            <p className="mt-3 rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
              No classes yet. Create one in the Classes tab.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
              {roster.classes.map((c) => {
                const currentLead = c.class_teacher_id && c.class_teacher_id !== teacher.id ? roster.teacherById.get(c.class_teacher_id) : null;
                return (
                  <li key={c.id} className="flex items-center gap-3 px-3 py-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={teaches.has(c.id)}
                      onChange={() => toggleTeaches(c.id)}
                      disabled={busy}
                      aria-label={`Teaches ${c.class_name}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-foreground">
                        {c.class_name}
                        {c.section ? ` ${c.section}` : ''}
                        {c.subject ? <span className="text-muted-foreground"> · {c.subject}</span> : null}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {(c.student_ids ?? []).length}/{MAX_CLASS_SIZE} students
                        {currentLead ? ` · class teacher: ${currentLead.name}` : ''}
                      </p>
                    </div>
                    <label className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-primary"
                        checked={leads.has(c.id)}
                        onChange={() => toggleLeads(c.id)}
                        disabled={busy}
                      />
                      Class teacher
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {[...leads].some((id) => {
            const c = roster.classById.get(id);
            return c?.class_teacher_id && c.class_teacher_id !== teacher.id;
          }) && <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">This replaces the current class teacher where one is set.</p>}
        </section>

        {confirming && (
          <section className="mt-5 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
            <p className="text-sm font-medium text-foreground">
              {confirming === 'reject' ? `Reject ${teacher.name}?` : `Revoke ${teacher.name}'s approval?`}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {confirming === 'reject'
                ? 'They will not be able to teach at your school. You can approve them later.'
                : 'They lose access and are taken off every class they teach here.'}
            </p>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional, kept on record)"
              className="mt-2 min-h-[60px] text-sm"
              maxLength={500}
              disabled={busy}
            />
          </section>
        )}

        <div className="mt-auto flex flex-wrap justify-end gap-2 border-t border-border pt-4">
          {confirming ? (
            <>
              <Button variant="ghost" onClick={() => setConfirming(null)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={() => decline(confirming)} disabled={busy}>
                {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {confirming === 'reject' ? 'Reject teacher' : 'Revoke approval'}
              </Button>
            </>
          ) : isApproved ? (
            <>
              <Button variant="outline" className="mr-auto text-destructive" onClick={() => setConfirming('revoke')} disabled={busy}>
                Revoke approval
              </Button>
              <Button onClick={saveClasses} disabled={busy || !dirty}>
                {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Save classes
              </Button>
            </>
          ) : (
            <>
              {status !== 'rejected' && (
                <Button variant="outline" className="mr-auto text-destructive" onClick={() => setConfirming('reject')} disabled={busy}>
                  Reject
                </Button>
              )}
              <Button onClick={approve} disabled={busy}>
                {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {teaches.size ? `Approve & assign ${teaches.size}` : 'Approve'}
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
